import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CircleAlert, Info, Mail } from 'lucide-react';
import { requestPasswordReset } from '../remote';

const ForgotPassword: React.FC = () => {
  const [identifier, setIdentifier] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const send = async () => {
    setError(null);
    const trimmed = identifier.trim();
    if (!trimmed) {
      setError('Please enter your email or username.');
      return;
    }
    setLoading(true);
    try {
      const payload: any = trimmed.includes('@') ? { email: trimmed } : { username: trimmed };
      // The response is deliberately ignored: the backend never reveals whether
      // an account exists, and the reset link is delivered by email only.
      await requestPasswordReset(payload);
    } catch {
      // Same outcome on failure so the form cannot be used to probe accounts.
    } finally {
      setSentTo(trimmed);
      setLoading(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void send();
  };

  const back = (
    <Link to="/login" className="mb-[26px] inline-flex min-h-[32px] items-center gap-1.5 text-[13px] font-semibold text-muted-2 no-underline hover:text-ink">
      <ArrowLeft size={18} strokeWidth={1.8} aria-hidden />Back to sign in
    </Link>
  );

  if (sentTo !== null) {
    return (
      <div>
        {back}
        <div role="status">
          <div className="mb-[22px] grid h-14 w-14 place-items-center rounded-[18px] bg-success-bg text-success"><Mail size={26} strokeWidth={1.8} aria-hidden /></div>
          <h1 className="m-0 font-display text-[34px] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink">Check your inbox</h1>
          <p className="mb-0 mt-2.5 text-[15px] text-muted">
            If an account exists for <strong className="text-ink">{sentTo}</strong>, reset instructions are on their way.
          </p>
          <button type="button" onClick={() => { setSentTo(null); setIdentifier(''); setError(null); }}
            className="mt-[22px] flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[12px] border border-line-strong bg-surface text-[15px] font-semibold text-ink hover:border-ink">
            Use a different account
          </button>
          <p className="mb-0 mt-[22px] text-[13px] text-muted">
            Nothing after 5 minutes? Check spam, or{' '}
            <button type="button" onClick={() => void send()} disabled={loading} className="cursor-pointer border-0 bg-transparent p-0 text-[13px] font-semibold text-ink underline disabled:opacity-60">send it again</button>.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {back}
      <h1 className="m-0 font-display text-[34px] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink">Forgot your password?</h1>
      <p className="mb-[30px] mt-2.5 text-[15px] text-muted">Enter the email or username on your account and we will send you a link to set a new one.</p>
      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="forgot-identifier" className="mb-2 block font-semibold text-ink">Email or username</label>
        <div className={`flex min-h-[48px] items-center gap-2 rounded-[12px] border bg-surface px-3.5 focus-within:border-ink focus-within:ring-[3px] focus-within:ring-accent/70 ${error ? 'border-danger' : 'border-line-strong'}`}>
          <Mail size={18} strokeWidth={1.8} className="text-muted" aria-hidden />
          <input
            id="forgot-identifier"
            type="text"
            autoComplete="username"
            value={identifier}
            onChange={(e) => { setIdentifier(e.target.value); setError(null); }}
            placeholder="e.g. selam.a or selam@eltest.edu.et"
            aria-describedby={error ? 'forgot-identifier-err' : undefined}
            className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[15px] text-ink shadow-none outline-none ring-0 focus:ring-0"
          />
        </div>
        {error && (
          <div id="forgot-identifier-err" role="alert" className="mt-2.5 flex items-start gap-2 text-[13px] text-danger-ink">
            <CircleAlert size={16} strokeWidth={1.8} className="mt-px flex-none" aria-hidden />{error}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          className="mt-[22px] flex min-h-[50px] w-full items-center justify-center gap-2 rounded-[12px] bg-navy text-[15px] font-semibold text-white hover:text-accent disabled:opacity-60"
        >
          {loading ? 'Sending…' : 'Send reset link'}
          <ArrowRight size={18} strokeWidth={1.8} aria-hidden />
        </button>
        <div className="mt-[22px] flex gap-2.5 rounded-[14px] bg-mist p-3.5 text-[13px] text-muted-2">
          <Info size={18} strokeWidth={1.8} className="mt-px flex-none" aria-hidden />
          <span>Students and teachers without an email can ask the school admin to reset their password from the panel.</span>
        </div>
      </form>
    </div>
  );
};

export default ForgotPassword;
